import { utcToLADateKey, addDaysToLADateKey, buildDateKeys } from './laDateUtils';

/**
 * Assigns each booking group to at most one stay period. Periods are walked in
 * check-in date order (not raw array order, which isn't guaranteed chronological)
 * so an earlier-starting period claims a group before a later one can. Results are
 * keyed by the period's original array index so "Stay 1"/"Stay 2" labels stay stable.
 * Groups claimed by no period are returned separately instead of silently discarded.
 */
export function assignGroupsToPeriods(groups, periods) {
  const indexed = periods.map((period, index) => ({
    index,
    inKey: utcToLADateKey(period.check_in_date),
    outKey: utcToLADateKey(period.check_out_date),
  }));
  const byCheckIn = [...indexed].sort((a, b) => (a.inKey < b.inKey ? -1 : a.inKey > b.inKey ? 1 : 0));

  const claimed = new Set();
  const periodGroups = new Map();
  for (const { index, inKey, outKey } of byCheckIn) {
    const claimedHere = groups.filter(
      (g) => !claimed.has(g) && g.from_date >= inKey && g.to_date < outKey
    );
    claimedHere.forEach((g) => claimed.add(g));
    periodGroups.set(index, claimedHere);
  }

  const orphanGroups = groups.filter((g) => !claimed.has(g));
  return { periodGroups, orphanGroups };
}

/**
 * How many of a stay period's nights are covered by its assigned room segments.
 * Intersects each segment's night-key range against the period's own night-key set,
 * so a segment that overhangs the period on one side can't inflate the count —
 * correct regardless of whether the caller already guaranteed containment.
 */
export function computeCoverage(period, assignedGroups) {
  const inKey = utcToLADateKey(period.check_in_date);
  const outKey = utcToLADateKey(period.check_out_date);
  const periodKeys = buildDateKeys(inKey, outKey);
  const periodKeySet = new Set(periodKeys);

  const covered = new Set();
  for (const group of assignedGroups) {
    const groupKeys = buildDateKeys(group.from_date, addDaysToLADateKey(group.to_date, 1));
    for (const key of groupKeys) {
      if (periodKeySet.has(key)) covered.add(key);
    }
  }

  const totalNights = periodKeys.length;
  const coveredNights = covered.size;
  return { coveredNights, totalNights, isFull: totalNights > 0 && coveredNights >= totalNights };
}
